/**
 * Cross-tab change notice.
 *
 * IndexedDB is shared between tabs. The in-memory signals that every screen
 * reads are not — each tab has its own copy, loaded when that tab booted.
 *
 * Proven on Phil's browser 2026-07-27. Two tabs were open. One sat on the
 * Settings screen from 23:32. The other made five jobs, which advanced the bid
 * counter from 1004 to 1008. At 00:54 David's phone and email were typed into
 * the first tab and saved, and that save wrote back the whole settings record
 * as that tab had known it at 23:32 — counter included. Two jobs then received
 * bid numbers that were already in use, and a bid number prints on a proposal.
 *
 * The snapshot taken at 00:54 still carried lastSnapshotAt = 23:32:21, which
 * is what made the age of the copy visible rather than merely suspected.
 *
 * Storage is now authoritative for anything the app owns (see MACHINE_OWNED in
 * db.ts), so a stale write can no longer move a counter. This channel closes
 * the rest of the gap: a tab that hears a change reloads, so what is on screen
 * is what is in the database.
 */
const CHANNEL = 'fgbs-changed';

let chan: BroadcastChannel | null = null;
let unavailable = false;

function channel(): BroadcastChannel | null {
  if (unavailable) return null;
  if (chan) return chan;
  try {
    if (typeof BroadcastChannel === 'undefined') { unavailable = true; return null; }
    chan = new BroadcastChannel(CHANNEL);
    return chan;
  } catch { unavailable = true; return null; }
}

/** Tell other tabs that something in the database moved. Never throws. */
export function announceChange(): void {
  try { channel()?.postMessage(1); } catch { /* a tab that cannot announce still works alone */ }
}

/**
 * Run `fn` when another tab reports a change. A BroadcastChannel does not
 * deliver to the tab that posted, so this cannot feed itself. Bursts are
 * collapsed — a bulk import is one reload, not one per row.
 */
export function onChangeFromOtherTab(fn: () => void, waitMs = 150): () => void {
  const c = channel();
  if (!c) return () => { /* nothing subscribed */ };
  let timer: ReturnType<typeof setTimeout> | null = null;
  const handler = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, waitMs);
  };
  c.addEventListener('message', handler);
  return () => {
    if (timer) clearTimeout(timer);
    c.removeEventListener('message', handler);
  };
}

/** test hook */
export function _resetBus(): void {
  try { chan?.close(); } catch { /* already closed */ }
  chan = null; unavailable = false;
}
